import { useEffect, useState } from 'react'
import { resolveApiBase } from './api'
import { LiveDeskPage } from './LiveDeskPage'
import type { ShareResolveResponse, ShareSnapshot } from './types'
import SnapshotView from './SnapshotView'
import AddToWorkspace from './AddToWorkspace'
import TestAccountSignup from './TestAccountSignup'
import { canOpenInBrowser, openInBrowserUrl } from './openInBrowser'
import { safeHref } from './safeUrl'

// Public viewer for Plexii share links.
// Reads :token from the URL path (/share/<token>), fetches the snapshot
// from the signal server, and renders it as readable HTML. No login
// required for read-only viewing.
//
// For a desk shared with scope='copy', the first thing on the page is "Open
// this desk in your browser": the share app on plexiidesk.com opens the same
// token as a working desk, no account and no install (openInBrowser.ts). The
// older route -- sign up here so the share lands in a "Shared with me" inbox,
// then install the app -- stays below it as the way to keep the desk in an
// account.

// Server base URL — built into the bundle at build time. Resolved by the same
// function the Vite config uses to write the API origin into the page's
// Content-Security-Policy (src/api.ts), so the two cannot drift apart.
const API_BASE = resolveApiBase(import.meta.env.VITE_API_BASE, import.meta.env.PROD)

// Download URL for PlexiDesk desktop — set at build time. The viewer's
// "Add to workspace" flow lands the user here once they've signed up,
// with their inbox already populated server-side. When they install the
// desktop app and sign in, their shared items appear automatically.
// focusbuddy.app is NOT ours — it serves a different, live product called
// Focus Buddy. This fallback, and the brand links below, were sending every
// visitor who opened a shared desk link straight to a third party, including
// from a button marked "download". The viewer is the one surface a PROSPECT
// sees before they have the app, so it was leaking exactly the people the
// share link was meant to convert.
// Deliberately NOT read from VITE_FOCUSBUDDY_DOWNLOAD any more. That name was
// set to the third-party URL in wrangler.toml, and this read took the env var
// in preference to the fallback — so any build that picked the config up would
// reinstate the leak silently. Renaming it means a stale value under the old
// name is ignored rather than obeyed, and the worst case is this safe default.
const PLEXII_DOWNLOAD_URL =
  (import.meta.env.VITE_PLEXII_DOWNLOAD as string | undefined) ||
  'https://plexiidesk.com/download'
const PLEXII_SITE_URL = 'https://plexiidesk.com'
// The share app that opens a copyable desk in the browser (openInBrowser.ts).
const SHARE_APP_URL = import.meta.env.VITE_SHARE_APP_URL as string | undefined

function parseTokenFromPath(): string | null {
  // Accept both /share/<token> and /s/<token>: the brochure's SharedView iframes
  // the viewer at /s/<token>, while direct links use /share/<token>.
  // Underscores and hyphens are part of the token alphabet: server-minted
  // live-desk tokens are prefixed (dsk_...), and a character class of only
  // [a-z0-9] silently truncated them to the prefix.
  const m = window.location.pathname.match(/\/(?:share|s)\/([A-Za-z0-9_-]{6,128})/)
  return m ? m[1] : null
}

export default function App(): JSX.Element {
  const [token] = useState(parseTokenFromPath)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [snapshot, setSnapshot] = useState<ShareSnapshot | null>(null)
  // A live desk is a different surface entirely: a continuously republished
  // projection rendered on a read-only canvas, not a one-off snapshot.
  const [isLiveDesk, setIsLiveDesk] = useState(false)
  const [meta, setMeta] = useState<{
    kind: string
    scope: 'view' | 'copy'
    fromHandle: string
    viewCount: number
    createdAt: number
  } | null>(null)

  useEffect(() => {
    if (!token) {
      // No share token = someone landed on the viewer root. Instead of an
      // error, we show the test-account signup landing (see render below).
      setLoading(false)
      return
    }
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/share/${token}`)
        if (!res.ok) {
          if (res.status === 404) {
            setError(
              'This link has expired, been revoked, or never existed. Ask the person who shared it for a new one.'
            )
          } else {
            setError(`The server returned ${res.status}. Try again in a moment.`)
          }
          return
        }
        const json = (await res.json()) as ShareResolveResponse
        if (!json.ok || !json.record) {
          setError(json.error || 'Could not load the share.')
          return
        }
        if (json.record.kind === 'desk') {
          // Live desks render on the read-only canvas, which fetches the
          // projection and follows revisions itself.
          setIsLiveDesk(true)
          return
        }
        setSnapshot(json.record.snapshot)
        setMeta({
          kind: json.record.kind,
          scope: json.record.scope,
          fromHandle: json.record.fromHandle,
          viewCount: json.record.viewCount,
          createdAt: json.record.createdAt
        })
      } catch (err) {
        setError(`Could not reach the Plexii server: ${(err as Error).message}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [token])

  if (isLiveDesk && token) return <LiveDeskPage apiBase={API_BASE} token={token} />

  // A copyable desk opens in the browser; when it does, that is the one primary
  // action on the page and everything else is secondary to it.
  const openUrl =
    snapshot && meta && token && canOpenInBrowser(meta.kind, meta.scope, token)
      ? safeHref(openInBrowserUrl(SHARE_APP_URL, token) ?? '')
      : null

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <header className="px-6 py-3 flex items-center justify-between border-b border-white/[0.04]">
        <a
          href={PLEXII_SITE_URL}
          className="inline-flex items-center gap-2 text-sm font-semibold tracking-[0.18em] text-stone-100 select-none hover:text-white transition-colors"
        >
          <span className="text-accent">✦</span>
          <span>PLEXII</span>
          <span className="text-[10px] font-mono text-accent px-1 py-px rounded bg-accent/10 border border-accent/20">
            shared
          </span>
        </a>
        <a
          href={PLEXII_DOWNLOAD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`${openUrl ? 'btn-ghost' : 'btn-primary'} !py-1.5 !px-3 !text-[12px]`}
        >
          Get Plexii
        </a>
      </header>

      <main className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-3xl mx-auto">
          {loading && <LoadingState />}
          {!loading && !token && (
            <LandingSignup apiBase={API_BASE} downloadUrl={PLEXII_DOWNLOAD_URL} />
          )}
          {error && <ErrorState error={error} />}
          {snapshot && meta && (
            <>
              <ShareMeta meta={meta} />
              {openUrl && <OpenInBrowser url={openUrl} downloadUrl={PLEXII_DOWNLOAD_URL} />}
              <SnapshotView
                snapshot={snapshot}
                blobUrl={token ? `${API_BASE}/share/${token}/blob` : undefined}
              />
              {meta.scope === 'copy' && token && (
                <AddToWorkspace
                  token={token}
                  apiBase={API_BASE}
                  downloadUrl={PLEXII_DOWNLOAD_URL}
                  secondary={!!openUrl}
                />
              )}
              {meta.scope === 'view' && (
                <div className="mt-8 glass-card rounded-xl p-5 text-center">
                  <h3 className="text-sm font-medium text-stone-100 mb-1">
                    Want to do this too?
                  </h3>
                  <p className="text-[12px] text-stone-400 mb-3">
                    Plexii gives every piece of work its own desk — docs, sheets,
                    slides, meetings and mail in one place. Free to download, and your
                    data stays on your machine.
                  </p>
                  <a href={PLEXII_DOWNLOAD_URL} className="btn-primary inline-block">
                    Get Plexii
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <footer className="px-6 py-4 border-t border-white/[0.04] text-[11px] text-stone-500 flex items-center justify-between">
        <span>Shared via Plexii</span>
        <a href={PLEXII_SITE_URL} className="hover:text-white">
          plexiidesk.com
        </a>
      </footer>
    </div>
  )
}

// The page's one primary action for a copyable desk: a working copy of it, in
// the browser, before anything is asked of the visitor.
function OpenInBrowser({ url, downloadUrl }: { url: string; downloadUrl: string }): JSX.Element {
  return (
    <div className="mb-4 glass-card rounded-2xl p-6" data-testid="open-in-browser">
      <h2 className="text-base font-semibold text-stone-100 mb-1">This desk can be yours</h2>
      <p className="text-[13px] text-stone-400 leading-relaxed mb-4">
        Open a working copy of it right here in your browser: move things, type, try it. No account,
        nothing to install, and nothing you change reaches the person who sent it.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <a href={url} className="btn-primary inline-block" data-testid="open-in-browser-link">
          Open this desk in your browser
        </a>
        <a
          href={downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[12px] text-stone-400 hover:text-stone-100 transition-colors"
        >
          or download Plexii for desktop
        </a>
      </div>
    </div>
  )
}

function ShareMeta({
  meta
}: {
  meta: { scope: 'view' | 'copy'; fromHandle: string; viewCount: number; createdAt: number }
}): JSX.Element {
  const ageMs = Date.now() - meta.createdAt
  const days = Math.floor(ageMs / 86_400_000)
  const hours = Math.floor((ageMs % 86_400_000) / 3_600_000)
  const ageLabel =
    days > 0 ? `${days}d ago` : hours > 0 ? `${hours}h ago` : 'just now'
  return (
    <div className="mb-4 flex items-center justify-between text-[11px] text-stone-500">
      <div className="inline-flex items-center gap-1.5">
        <span>Shared by</span>
        <span className="font-mono text-stone-300">{meta.fromHandle}</span>
        <span>·</span>
        <span>{ageLabel}</span>
      </div>
      <div className="inline-flex items-center gap-2">
        <span>{meta.viewCount} {meta.viewCount === 1 ? 'view' : 'views'}</span>
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] ${
            meta.scope === 'copy'
              ? 'bg-accent/15 text-accent border border-accent/20'
              : 'bg-white/[0.05] text-stone-400 border border-white/[0.08]'
          }`}
        >
          {meta.scope === 'copy' ? 'Can be added to workspace' : 'View only'}
        </span>
      </div>
    </div>
  )
}

// Shown at the viewer root (no share token). Doubles the viewer as a
// self-serve test-account portal: create an account here, then download the
// desktop app and sign in to use the system.
function LandingSignup({
  apiBase,
  downloadUrl
}: {
  apiBase: string
  downloadUrl: string
}): JSX.Element {
  return (
    <div className="py-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold text-stone-100 mb-2">Try Plexii</h1>
        <p className="text-[13px] text-stone-400 leading-relaxed max-w-md mx-auto">
          Create a test account, then download the desktop app and sign in with the same
          email and password. Your workspace stays on your machine and syncs through this
          test server.
        </p>
      </div>
      <TestAccountSignup apiBase={apiBase} downloadUrl={downloadUrl} />
    </div>
  )
}

function LoadingState(): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-stone-400">
      <div className="h-10 w-10 rounded-full border-2 border-accent/30 border-t-accent animate-spin mb-3" />
      <p className="text-[13px]">Loading the share…</p>
    </div>
  )
}

function ErrorState({ error }: { error: string }): JSX.Element {
  return (
    <div className="glass-card rounded-2xl p-8 text-center">
      <div
        className="h-12 w-12 rounded-2xl mx-auto mb-4 inline-flex items-center justify-center text-xl"
        style={{
          background: 'linear-gradient(135deg, rgba(244, 114, 182, 0.25), rgba(244, 114, 182, 0.10))',
          color: 'rgb(251, 207, 232)'
        }}
      >
        !
      </div>
      <h2 className="text-base font-semibold text-stone-100 mb-1">
        Can't load this share
      </h2>
      <p className="text-[13px] text-stone-400 leading-relaxed max-w-md mx-auto">
        {error}
      </p>
    </div>
  )
}
